import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo } from '../model/metaInfo';
import type {
  InferenceInvokeModelRequest,
  InferenceInvokeModelResponse,
  InferenceConverseRequest,
  InferenceConverseResponse,
} from '../model/types';

/**
 * Inference (foundation-model) client. Calls {@link ApiClient} only.
 * Pure TS — no React. jobCategory is AI_ASSIST for all inference requests.
 */

const PATHS = {
  invokeModel: '/inference/invoke-model',
  converse: '/inference/converse',
} as const;

export interface InvokeModelArgs {
  modelId: string;
  body: string;
  contentType?: string;
  accept?: string;
}

export interface ConverseArgs {
  modelId: string;
  prompt: string;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
}

export class InferenceApi {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
  ) {}

  async invokeModel(
    args: InvokeModelArgs,
  ): Promise<InferenceInvokeModelResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'InferenceInvokeModel',
      jobCategory: 'AI_ASSIST',
      requestType: 'INVOKE_MODEL',
    });
    return this.apiClient.post<
      InferenceInvokeModelRequest,
      InferenceInvokeModelResponse
    >(PATHS.invokeModel, {
      metaInfo,
      modelId: args.modelId,
      body: args.body,
      ...(args.contentType ? { contentType: args.contentType } : {}),
      ...(args.accept ? { accept: args.accept } : {}),
    });
  }

  async converse(args: ConverseArgs): Promise<InferenceConverseResponse> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'InferenceConverse',
      jobCategory: 'AI_ASSIST',
      requestType: 'CONVERSE',
    });
    return this.apiClient.post<
      InferenceConverseRequest,
      InferenceConverseResponse
    >(PATHS.converse, {
      metaInfo,
      modelId: args.modelId,
      prompt: args.prompt,
      ...(args.systemPrompt ? { systemPrompt: args.systemPrompt } : {}),
      ...(args.maxTokens !== undefined ? { maxTokens: args.maxTokens } : {}),
      ...(args.temperature !== undefined
        ? { temperature: args.temperature }
        : {}),
    });
  }
}
